import { createContext, useContext, useEffect, useState } from "react";

/**
 * MapPerformanceContext — styrer Light Mode for kortet.
 *
 * Hvad Light Mode slår FRA (for hurtigere ydeevne på langsomme enheder):
 *   1. Map zoom/pan animationer
 *   2. Marker-cluster animationer
 *   3. SVG-renderer (bruger Canvas i stedet)
 *   4. Standard cluster-radius (bruger 100px i stedet for 50/80)
 *   5. Fade-transitions på popups og knapper (CSS klasse `light-mode` på <html>)
 *
 * Bevidst IKKE inkluderet i Light Mode (efter brugerens ønske):
 *   - GPS-tracker polling (forbliver 15s)
 *   - Map-overlay kvalitet (forbliver upåvirket)
 *   - Live monitor klokke-pulse (forbliver upåvirket)
 *
 * Default: Auto-aktiveret på tablet/mobil (< 1024px) første gang.
 *          Brugeren kan manuelt slå FRA hvis de har en hurtig enhed.
 */

const STORAGE_KEY = 'resovia_light_mode_v1';
const MapPerformanceContext = createContext(null);

function getInitialLightMode() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
    // Første besøg: auto-aktiver på tablet/mobil
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

export function MapPerformanceProvider({ children }) {
  const [lightMode, setLightModeState] = useState(getInitialLightMode);

  useEffect(() => {
    // Sæt CSS-klasse på <html> så vi kan bruge `.light-mode *` i CSS for at deaktivere fade-transitions
    if (typeof document !== 'undefined') {
      document.documentElement.classList.toggle('light-mode', lightMode);
    }
  }, [lightMode]);

  const setLightMode = (val) => {
    setLightModeState(val);
    try { localStorage.setItem(STORAGE_KEY, val ? 'true' : 'false'); } catch {}
  };

  const toggleLightMode = () => setLightMode(!lightMode);

  return (
    <MapPerformanceContext.Provider value={{ lightMode, setLightMode, toggleLightMode }}>
      {children}
    </MapPerformanceContext.Provider>
  );
}

export function useMapPerformance() {
  const ctx = useContext(MapPerformanceContext);
  if (!ctx) {
    // Defensiv: hvis bruger glemmer at wrappe, returnér default uden at crashe
    return { lightMode: false, setLightMode: () => {}, toggleLightMode: () => {} };
  }
  return ctx;
}
